import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  map,
  scan,
  shareReplay,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { ApplicationService } from '../../../services/application.service';
import { Application } from '../../../interfaces/application.interface';
import { LinkTypeService } from '../../../services/link-type.service';
import { LogMatcherContextService } from '../../../services/logmatcher-context.service';
import { TourOverlayComponent } from '../../../tour/tour-overlay.component';
import { TourService, TourStep } from '../../../services/tour.service';
import { showErrorSwal } from '@asamco/utils';
import { JobLogPatternService, JobLogService, LogRow, PatternStatus } from '@asamco/application-link/shared';
import { PagedResult } from 'libs/application-link/shared/src/lib/interfaces/paged-result.interface';

type UniqueMessageGroup = {
  message: string;
  count: number;
  lastSeen: string; // keep as string since your creationDate is string
  sampleRow: UiLogRow; // for pills (apps/linktype) + copy
};

export interface LinkType {
  description: string;
  id: number;
}

type SearchAction =
  | { kind: 'search'; reset: true }
  | { kind: 'more'; reset: false };

type UiLogRow = LogRow & {
  fromAppName?: string;
  toAppName?: string;
  linkTypeName?: string;
};

@Component({
  selector: 'appshell-link-joblog-search-admin',
  standalone: true,
  imports: [CommonModule, FormsModule, TourOverlayComponent],
  templateUrl: './link-joblog-search-admin.component.html',
  styleUrl: './link-joblog-search-admin.component.scss',
})
export class LinkJoblogSearchAdminComponent implements OnInit, OnDestroy {
  // ====== form model ======
  pattern =
    `Setting to use internal product IDs is enabled, but no internal product ID is specified for the product (<VAR>)) on billing item '<VAR>'. Falling back on external number from billing code.`;

  searchInStackTrace = false;

  enforceOrder = true;
  take = 100;
  linkId: number | null = null;

  // IMPORTANT: default should be Error(4)
  jobLogType: number | null = 4;

  fromApplicationId: number | null = null;
  toApplicationId: number | null = null;

  // Pattern “draft” fields
  patternTitle = '';
  patternCategory: string | null = null;
  patternStatus: PatternStatus = 1;

  // NEW fields
  description = '';
  displayMessage = '';
  articleIdsText = ''; // raw input, e.g. "123, 456"

  saveMessage$ = new BehaviorSubject<string>('');
  readonly loading$ = new BehaviorSubject<boolean>(false);
  readonly error$ = new BehaviorSubject<string>('');

  seedHint = false;

  previewOpen = false;
  testOpen = false;

  uniqueOnly = false;

  // UX: collapse create-area by default (search-first)
  createPanelOpen = false;

  // ====== internal UI streams ======
  private readonly action$ = new Subject<SearchAction>();
  private readonly subs = new Subscription();

  // ✅ paging state
  private readonly skip$ = new BehaviorSubject<number>(0);

  // ✅ total from backend (only updated on new searches)
  readonly total$ = new BehaviorSubject<number>(0);

  private lastChunks: string[] = [];
  private lastTerms: string[] = [];

  showUniqueMessages = false;

  toggleUniqueMessages() {
    this.showUniqueMessages = !this.showUniqueMessages;
  }

  // Expose for template (preview UI)
  get chunksPreview(): string[] {
    return this.lastChunks;
  }
  get termsPreview(): string[] {
    return this.lastTerms;
  }

  // Derived form helpers
  get patternTrimmed(): string {
    return (this.pattern ?? '').trim();
  }
  get titleTrimmed(): string {
    return (this.patternTitle ?? '').trim();
  }
  get canSearch(): boolean {
    return !!this.patternTrimmed && this.lastTerms.length > 0 && !this.loading$.value;
  }
  get canCreateOrSave(): boolean {
    return !!this.patternTrimmed && !!this.titleTrimmed && this.lastTerms.length > 0 && !this.loading$.value;
  }
  get titleError(): string | null {
    if (!this.createPanelOpen) return null;
    if (!this.patternTrimmed) return 'Paste a log pattern first.';
    if (!this.titleTrimmed) return 'Pattern title is required to create/save.';
    if (this.lastTerms.length === 0) return 'Pattern is too generic — add more static text outside <VAR>.';
    return null;
  }
  get patternGenericError(): string | null {
    if (!this.patternTrimmed) return null;
    return this.lastTerms.length === 0 ? 'Pattern is too generic — add more static text outside <VAR>.' : null;
  }

  readonly jobLogTypeOptions = [
    { value: 0, label: 'Undefined' },
    { value: 1, label: 'Success' },
    { value: 2, label: 'Fail' },
    { value: 3, label: 'AmountMismatch' },
    { value: 4, label: 'Error' },
    { value: 5, label: 'All' },
    { value: 6, label: 'Info' },
  ] as const;

  // Applications list (sorted)
  readonly applications$: Observable<Application[]> = this.applicationService.get().pipe(
    map(apps =>
      [...apps].sort((a, b) =>
        (a.displayName || a.name || '').localeCompare(b.displayName || b.name || '', undefined, {
          sensitivity: 'base',
        })
      )
    ),
    catchError(() => of([])),
    shareReplay({ bufferSize: 1, refCount: true })
  );


  // App name lookup
  private readonly appNameById$ = this.applications$.pipe(
    map(apps => {
      const m = new Map<number, string>();
      for (const a of apps) m.set(a.id, a.displayName || a.name || `#${a.id}`);
      return m;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Link types list + lookup
  readonly linkTypes$: Observable<LinkType[]> = this.linkTypeService.getAllLinkTemplates().pipe(
    catchError(() => of([])),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly linkTypeNameById$ = this.linkTypes$.pipe(
    map(items => {
      const m = new Map<number, string>();
      for (const lt of items) m.set(lt.id, lt.description || `#${lt.id}`);
      return m;
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private readonly lookups$ = combineLatest([this.appNameById$, this.linkTypeNameById$]).pipe(
    map(([appMap, linkTypeMap]) => ({ appMap, linkTypeMap })),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  // Main results stream
  readonly results$: Observable<UiLogRow[]> = this.action$.pipe(
    tap(action => {
      this.error$.next('');
      this.saveMessage$.next('');

      if (action.kind === 'search') {
        const p = this.patternTrimmed;
        if (!p) {
          this.error$.next('Please paste a pattern.');
          this.lastChunks = [];
          this.lastTerms = [];
          return;
        }

        this.lastChunks = extractChunks(p);
        this.lastTerms = extractTerms(this.lastChunks);

        if (this.lastTerms.length === 0) {
          this.error$.next('Pattern is too generic. Add more static text outside <VAR>.');
          return;
        }
      }
    }),
    withLatestFrom(this.skip$),
    switchMap(([action, skip]) => {
      if (!this.patternTrimmed) {
        return of({ action, rows: [] as LogRow[], total: 0, error: 'Please paste a pattern.' });
      }
      if (this.lastTerms.length === 0) {
        return of({ action, rows: [] as LogRow[], total: 0, error: this.error$.value || 'Invalid pattern.' });
      }

      this.loading$.next(true);

      return this.logs
        .searchByUserPattern({
          terms: this.lastTerms,
          chunks: this.lastChunks,
          take: this.take,
          skip,
          linkId: this.linkId,
          jobLogType: this.jobLogType,
          fromApplicationId: this.fromApplicationId,
          toApplicationId: this.toApplicationId,
          linkTypeId: null,
          includeTotal: action.kind === 'search',
          uniqueOnly: this.uniqueOnly,
          searchInStackTrace: this.searchInStackTrace
        })
        .pipe(
          map((res: PagedResult<LogRow>) => ({ action, rows: res.rows ?? [], total: res.total ?? 0, error: '' })),
          catchError(() => of({ action, rows: [] as LogRow[], total: 0, error: 'Search failed.' })),
          tap(result => {
            this.loading$.next(false);
            if (result.error) this.error$.next(result.error);

            // ✅ important: only update total on fresh searches
            if (result.action.kind === 'search') this.total$.next(result.total);
          })
        );
    }),
    withLatestFrom(this.lookups$),
    scan((acc: UiLogRow[], [result, { appMap, linkTypeMap }]) => {
      const rowsWithNames: UiLogRow[] = result.rows.map(r => ({
        ...r,
        fromAppName: r.fromApplicationId != null ? appMap.get(r.fromApplicationId) : undefined,
        toAppName: r.toApplicationId != null ? appMap.get(r.toApplicationId) : undefined,
        linkTypeName: r.linkTypeId != null ? linkTypeMap.get(r.linkTypeId) : undefined,
      }));

      if (result.action.kind === 'search') return rowsWithNames;
      return acc.concat(rowsWithNames);
    }, [] as UiLogRow[]),
    startWith([] as UiLogRow[]),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly uniqueMessages$: Observable<UniqueMessageGroup[]> = this.results$.pipe(
    map(rows => {
      const byMsg = new Map<string, UniqueMessageGroup>();

      for (const r of rows) {
        const msg = (r.message ?? '').trim();
        if (!msg) continue;

        const existing = byMsg.get(msg);
        if (!existing) {
          byMsg.set(msg, {
            message: msg,
            count: 1,
            lastSeen: r.creationDate,
            sampleRow: r,
          });
        } else {
          existing.count += 1;

          // update lastSeen (string compare is okay for ISO timestamps)
          if ((r.creationDate ?? '') > (existing.lastSeen ?? '')) {
            existing.lastSeen = r.creationDate;
            existing.sampleRow = r; // keep the newest sample (nice UX)
          }
        }
      }

      // sort: most frequent first, then most recent
      return Array.from(byMsg.values()).sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return (b.lastSeen ?? '').localeCompare(a.lastSeen ?? '');
      });
    }),
    startWith([] as UniqueMessageGroup[]),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly matchesLabelCount$ = this.results$.pipe(
    map(r => r.length),
    startWith(0)
  );

  readonly uniqueLabelCount$ = this.uniqueMessages$.pipe(
    map(r => r.length),
    startWith(0)
  );

  readonly hasTotal$ = this.total$.pipe(map(t => t > 0), startWith(false));

  // ✅ show/hide load more based on server total
  readonly canLoadMore$ = this.results$.pipe(
    map(r => r.length),
    withLatestFrom(this.total$),
    map(([loaded, total]) => total > 0 && loaded < total),
    startWith(false)
  );

  constructor(
    private logs: JobLogService,
    private applicationService: ApplicationService,
    private patternService: JobLogPatternService,
    private linkTypeService: LinkTypeService,
    private router: Router,
    private route: ActivatedRoute,
    private ctx: LogMatcherContextService,
    private tour: TourService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    const f = this.ctx.filters$.value;

    this.linkId = f.linkId ?? null;
    this.jobLogType = f.jobLogType ?? 4;
    this.fromApplicationId = f.fromApplicationId ?? null;
    this.toApplicationId = f.toApplicationId ?? null;
    this.take = f.take ?? this.take;
    this.enforceOrder = true;

    this.recomputePatternPreview();

    const qpSub = this.route.queryParamMap.subscribe(qp => {
      if (qp.get('seed') === '1') {
        const msg = this.ctx.seedMessage$.value;
        if (msg) {
          this.pattern = msg;
          this.patternTitle = this.patternTitle || 'From unmatched';
          this.seedHint = true;
          this.createPanelOpen = true;
          this.recomputePatternPreview();
        }
      } else {
        this.seedHint = false;
      }

      const patternId = qp.get('patternId');
      if (patternId) {
        const d = this.ctx.draft$.value;
        if (d?.pattern) {
          this.pattern = d.pattern;
          this.patternTitle = d.title || this.patternTitle;
          this.patternCategory = d.category ?? this.patternCategory;
          this.patternStatus = d.status ?? this.patternStatus;

          this.linkId = d.linkId ?? this.linkId;
          this.jobLogType = (d.jobLogType ?? this.jobLogType) ?? 4;
          this.fromApplicationId = d.fromApplicationId ?? this.fromApplicationId;
          this.toApplicationId = d.toApplicationId ?? this.toApplicationId;

          this.createPanelOpen = true;
          this.recomputePatternPreview();
        }
      }
    });

    this.subs.add(qpSub);

    const keyHandler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'Enter') {
        e.preventDefault();
        this.search();
        return;
      }
      if (e.ctrlKey && e.shiftKey && (e.key === 'S' || e.key === 's')) {
        e.preventDefault();
        this.saveAsPattern();
      }
    };
    window.addEventListener('keydown', keyHandler);
    this.subs.add({ unsubscribe: () => window.removeEventListener('keydown', keyHandler) });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

startTour() {
  // Reset optional UI to a clean baseline
  this.previewOpen = false;
  this.testOpen = false;
  this.createPanelOpen = false;
  this.showUniqueMessages = false;
  this.cdr.detectChanges();

  const steps: TourStep[] = [
    // ------------------------------------------------
    // PATTERN INPUT
    // ------------------------------------------------
    {
      id: 'pattern',
      target: '#tour-pattern',
      title: '1) Define a Log Pattern',
      body:
        'Paste a real log message and replace variable parts with <VAR>.\n\n' +
        'Keep enough static text so the system can extract meaningful search terms.\n\n' +
        'If the pattern is too generic, it cannot be searched or saved.',
      placement: 'bottom',
      padding: 10
    },

    // ------------------------------------------------
    // FILTERS
    // ------------------------------------------------
    {
      id: 'filters',
      target: '#tour-filters',
      title: '2) Narrow the scope',
      body:
        'Use filters to limit results by Job Log Type, From/To Application and Take count.\n\n' +
        'These filters are also stored with the pattern if you save it.',
      placement: 'right',
      padding: 10
    },

    // ------------------------------------------------
    // QUERY PREVIEW
    // ------------------------------------------------
    {
      id: 'preview-toggle',
      target: '#tour-query-preview-toggle',
      title: '3) Query preview',
      body:
        'Shows how your pattern is interpreted:\n\n' +
        '• Static chunks (text outside <VAR>)\n' +
        '• Search terms (used to find candidates quickly)\n\n' +
        'If no terms appear, your pattern is too generic.',
      placement: 'bottom',
      waitForTargetMs: 3000,
      beforeShow: async () => {
        this.previewOpen = true;
        this.cdr.detectChanges();
      }
    },

    // ------------------------------------------------
    // STRICT MATCHING (ALWAYS ON)
    // ------------------------------------------------
    {
      id: 'strict-matching',
      target: '#tour-query-preview',
      title: '4) Strict matching (always on)',
      body:
        'Matches are always strict:\n\n' +
        '• Static chunks must appear in the message in the same order\n' +
        '• <VAR> represents any text between chunks\n\n' +
        'This prevents false positives like unrelated invoice/lines errors.',
      placement: 'right',
      padding: 10,
      waitForTargetMs: 2500
    },

    // ------------------------------------------------
    // UNIQUE (SERVER)
    // ------------------------------------------------
    {
      id: 'unique-server',
      target: '#tour-unique-toggle',
      title: '5) Unique (server-side)',
      body:
        'This asks the backend to return only one row per unique message.\n\n' +
        'Use this when many identical errors are logged.',
      placement: 'bottom',
      padding: 10
    },

    // ------------------------------------------------
    // SEARCH
    // ------------------------------------------------
    {
      id: 'search',
      target: '#tour-search-btn',
      title: '6) Run the search',
      body:
        'Click Search or press Ctrl + Enter.\n\n' +
        'Results appear below. Use Load more to fetch the next page.',
      placement: 'left',
      padding: 10
    },

    // ------------------------------------------------
    // MATCHES
    // ------------------------------------------------
    {
      id: 'matches',
      target: '#tour-matches',
      title: '7) Matches',
      body:
        'Each result shows:\n' +
        '• Job log ID\n' +
        '• Timestamp\n' +
        '• Application → Application\n' +
        '• Link type\n\n' +
        'You can copy the full message instantly.',
      placement: 'left',
      waitForTargetMs: 2500
    },

    // ------------------------------------------------
    // UNIQUE (CLIENT)
    // ------------------------------------------------
    {
      id: 'unique-client',
      target: '#tour-unique-messages-btn',
      title: '8) Unique messages (client-side)',
      body:
        'This groups the loaded results by message text and shows how often each occurred.\n\n' +
        'This is different from the server-side Unique toggle.',
      placement: 'left'
    },

    // ------------------------------------------------
    // TEST PATTERN
    // ------------------------------------------------
    {
      id: 'test-toggle',
      target: '#tour-test-toggle',
      title: '9) Test your pattern',
      body:
        'Paste a real log message and verify you get MATCH.\n\n' +
        'This lets you confirm the pattern works before saving it.',
      placement: 'bottom',
      beforeShow: async () => {
        this.testOpen = true;
        this.cdr.detectChanges();
      }
    },

    // ------------------------------------------------
    // CREATE PATTERN INTRO
    // ------------------------------------------------
    {
      id: 'create-open',
      target: '#tour-create-toggle',
      title: '10) Create a reusable pattern',
      body:
        'This saves your pattern configuration so:\n\n' +
        '• It is used for automatic matching\n' +
        '• Unmatched logs can be reduced\n' +
        '• Documentation can be linked',
      placement: 'bottom',
      beforeShow: async () => {
        this.createPanelOpen = true;
        this.cdr.detectChanges();
      }
    },

    // ------------------------------------------------
    // TITLE
    // ------------------------------------------------
    {
      id: 'create-title',
      target: '#tour-create-title',
      title: '11) Pattern title (required)',
      body:
        'Give the pattern a clear, searchable name.\n\n' +
        'Without a title, the pattern cannot be saved.',
      placement: 'right'
    },

    // ------------------------------------------------
    // DESCRIPTION
    // ------------------------------------------------
    {
      id: 'create-description',
      target: '#tour-create-description',
      title: '12) Description',
      body:
        'Explain what the error means and what action should be taken.\n\n' +
        'Think: "If you see this error, do this."',
      placement: 'right'
    },

    // ------------------------------------------------
    // FRESHDESK ARTICLE IDS
    // ------------------------------------------------
    {
      id: 'create-articles',
      target: '#tour-create-articles',
      title: '13) Link Freshdesk help articles',
      body:
        'You can attach Freshdesk Solution Articles to this pattern.\n\n' +
        'Example article ID:\n' +
        '103000374329\n\n' +
        'How to find it:\n' +
        'Open the Freshdesk article and copy the last number in the URL.\n\n' +
        'Example URL format:\n' +
        '.../solutions/articles/<ARTICLE_ID>\n\n' +
        'We use this ID to embed/link the article from the error so users can open help directly.',
      placement: 'top'
    },

    // ------------------------------------------------
    // SAVE VS EDITOR
    // ------------------------------------------------
    {
      id: 'create-actions',
      target: '#tour-create-actions',
      title: '14) Save or open the editor',
      body:
        '• Save Pattern → saves immediately.\n\n' +
        '• Open Pattern Editor → opens the Patterns page with this data pre-filled.\n\n' +
        'Shortcut: Ctrl + Shift + S',
      placement: 'top'
    }
  ];

  this.tour.start(steps, 0);
}


  onPatternChange(_: string) {
    this.error$.next('');
    this.saveMessage$.next('');
    this.recomputePatternPreview();
  }

  private recomputePatternPreview() {
    const p = this.patternTrimmed;
    if (!p) {
      this.lastChunks = [];
      this.lastTerms = [];
      return;
    }
    this.lastChunks = extractChunks(p);
    this.lastTerms = extractTerms(this.lastChunks);
  }

  private persistFilters() {
    this.ctx.filters$.next({
      linkId: this.linkId,
      jobLogType: this.jobLogType,
      fromApplicationId: this.fromApplicationId,
      toApplicationId: this.toApplicationId,
      take: this.take,
      enforceOrder: true,
    });
  }

  search() {
    this.persistFilters();
    this.recomputePatternPreview();

    if (!this.patternTrimmed) {
      this.error$.next('Please paste a pattern.');
      return;
    }
    if (this.lastTerms.length === 0) {
      this.error$.next('Pattern is too generic. Add more static text outside <VAR>.');
      return;
    }

    // ✅ reset paging + total on a new search
    this.skip$.next(0);
    this.total$.next(0);

    this.action$.next({ kind: 'search', reset: true });
  }

  loadMore() {
    this.persistFilters();

    if (this.lastTerms.length === 0) {
      this.search();
      return;
    }

    // ✅ advance paging by current take
    this.skip$.next(this.skip$.value + this.take);

    this.action$.next({ kind: 'more', reset: false });
  }

  clearPatternOnly() {
    this.pattern = '';
    this.error$.next('');
    this.saveMessage$.next('');
    this.loading$.next(false);

    this.lastChunks = [];
    this.lastTerms = [];
    this.seedHint = false;

    // ✅ reset paging/total too
    this.skip$.next(0);
    this.total$.next(0);

    this.action$.next({ kind: 'search', reset: true });
  }

  resetFiltersToDefaults() {
    this.linkId = null;
    this.jobLogType = 4;
    this.fromApplicationId = null;
    this.toApplicationId = null;
    this.take = 100;
    this.enforceOrder = true;

    this.persistFilters();
  }

  goPatterns() {
    this.persistFilters();
    this.router.navigate(['../patterns'], { relativeTo: this.route });
  }

  goUnmatched() {
    this.persistFilters();
    this.router.navigate(['../unmatched'], { relativeTo: this.route });
  }

  openCreatePanel() {
    this.createPanelOpen = true;
  }

  toggleCreatePanel() {
    this.createPanelOpen = !this.createPanelOpen;
  }

  createPatternInPatterns() {
    this.saveMessage$.next('');
    this.error$.next('');
    this.recomputePatternPreview();

    this.createPanelOpen = true;

    const title = this.titleTrimmed || 'New pattern';
    const pattern = this.patternTrimmed;

    if (!pattern) {
      this.error$.next('Please paste a pattern first.');
      return;
    }
    if (!this.titleTrimmed) {
      this.error$.next('Pattern title is required to create a pattern.');
      return;
    }
    if (this.lastTerms.length === 0) {
      this.error$.next('Pattern is too generic. Add more static text outside <VAR>.');
      return;
    }

    this.persistFilters();

    const normalizedJobLogType = this.jobLogType === 5 ? null : this.jobLogType;

    this.ctx.draft$.next({
      title,
      pattern,
      category: (this.patternCategory ?? '').trim() || null,
      status: this.patternStatus,
      linkId: this.linkId,
      jobLogType: normalizedJobLogType,
      fromApplicationId: this.fromApplicationId,
      toApplicationId: this.toApplicationId,
      linkTypeId: null,
      description: (this.description ?? '').trim() || null,
      displayMessage: (this.displayMessage ?? '').trim() || null,
      freshdeskArticleIds: this.parseArticleIds(this.articleIdsText),
      searchInStackTrace: this.searchInStackTrace
    });

    this.router.navigate(['../patterns'], {
      relativeTo: this.route,
      queryParams: { mode: 'new' },
    });
  }

  parseArticleIds(input: string): number[] {
    return Array.from(
      new Set(
        (input ?? '')
          .split(/[\s,;]+/g)
          .map(s => s.trim())
          .filter(Boolean)
          .map(s => Number(s))
          .filter(n => Number.isFinite(n) && n > 0)
      )
    );
  }

  // --- Test Pattern UI ---
  testMessage = '';
  testResult: {
    isMatch: boolean;
    orderMatch: boolean;
    chunks: string[];
    terms: string[];
    notes?: string | null;
  } | null = null;

  testing$ = new BehaviorSubject<boolean>(false);
  testError$ = new BehaviorSubject<string>('');

  testPattern() {
    this.testError$.next('');
    this.testResult = null;

    const pattern = (this.pattern ?? '').trim();
    const message = (this.testMessage ?? '').trim();

    if (!pattern) {
      this.testError$.next('Paste a pattern first.');
      return;
    }
    if (!message) {
      this.testError$.next('Paste a log message to test against.');
      return;
    }

    this.testing$.next(true);

    this.patternService
      .test({
        pattern,
        message,
        enforceOrder: this.enforceOrder,
        jobLogType: this.jobLogType,
        fromApplicationId: this.fromApplicationId,
        toApplicationId: this.toApplicationId,
        linkTypeId: null,
      })
      .pipe(
        catchError(() => {
          this.testing$.next(false);
          this.testError$.next('Test failed.');
          return of(null);
        })
      )
      .subscribe(res => {
        this.testing$.next(false);
        if (!res) return;
        this.testResult = {
          isMatch: !!res.isMatch,
          orderMatch: !!res.orderMatch,
          chunks: res.chunks ?? [],
          terms: res.terms ?? [],
          notes: res.notes ?? null,
        };

        if (this.testResult.isMatch && !this.patternTitle?.trim()) {
          this.patternTitle = 'New pattern (tested)';
        }
      });
  }

  saveAsPattern() {
    this.saveMessage$.next('');
    this.error$.next('');
    this.recomputePatternPreview();

    this.createPanelOpen = true;

    const title = this.titleTrimmed;
    const pattern = this.patternTrimmed;

    if (!pattern) {
      this.error$.next('Please paste a pattern before saving.');
      return;
    }
    if (!title) {
      this.error$.next('Pattern title is required to save.');
      return;
    }
    if (this.lastTerms.length === 0) {
      this.error$.next('Pattern is too generic. Add more static text outside <VAR>.');
      return;
    }

    this.persistFilters();

    const normalizedJobLogType = this.jobLogType === 5 ? null : this.jobLogType;

    this.loading$.next(true);

    this.patternService
      .create({
        title,
        pattern,
        status: this.patternStatus,
        category: (this.patternCategory ?? '').trim() || null,
        linkId: this.linkId,
        jobLogType: normalizedJobLogType,
        fromApplicationId: this.fromApplicationId,
        toApplicationId: this.toApplicationId,
        linkTypeId: null,
        description: (this.description ?? '').trim() || null,
        displayMessage: (this.displayMessage ?? '').trim() || null,
        freshdeskArticleIds: this.parseArticleIds(this.articleIdsText),
        searchInStackTrace: this.searchInStackTrace
      })
      .pipe(
        catchError(error => {
          this.loading$.next(false);
          showErrorSwal(error);
          return of(null);
        })
      )
      .subscribe(res => {
        this.loading$.next(false);
        if (!res) return;
        this.saveMessage$.next(`Saved pattern #${res.id}.`);
      });
  }

  exportCsv() {
    let rows: UiLogRow[] = [];
    const sub = this.results$.subscribe(r => (rows = r));
    sub.unsubscribe();

    if (!rows.length) return;

    const esc = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const header = ['id', 'creationDate', 'fromApplicationId', 'toApplicationId', 'linkTypeId', 'message'].join(',');

    const lines = rows.map(r =>
      [esc(r.id), esc(r.creationDate), esc(r.fromApplicationId), esc(r.toApplicationId), esc(r.linkTypeId), esc(r.message)].join(',')
    );

    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = `logmatcher-matches-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();

    URL.revokeObjectURL(url);
  }

  copyToClipboard(text: string) {
    if (!text) return;
    navigator.clipboard?.writeText(text);
    this.saveMessage$.next('Copied to clipboard.');
    setTimeout(() => {
      if (this.saveMessage$.value === 'Copied to clipboard.') this.saveMessage$.next('');
    }, 1200);
  }

  copyPattern() {
    this.copyToClipboard(this.patternTrimmed);
  }
}

// ===== utilities =====
export function extractChunks(pattern: string): string[] {
  return pattern
    .split('<VAR>')
    .map(s => s.trim())
    .filter(s => s.length > 0);
}

export function extractTerms(chunks: string[]): string[] {
  const tokens = chunks
    .join(' ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter(t => t.length >= 4);

  return Array.from(new Set(tokens)).slice(0, 16);
}

export function matchesInOrder(message: string, chunks: string[]): boolean {
  let idx = 0;
  for (const ch of chunks) {
    const pos = message.indexOf(ch, idx);
    if (pos < 0) return false;
    idx = pos + ch.length;
  }
  return true;
}
